/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* eslint-disable no-unused-vars */
import { CombinedCommerceApiConfig, CustomerCredentials } from '~/lib/data-contract/salesforce';

import CommerceAPI from '.';
import { HTTPError } from './http-error';
import { createCodeVerifier, generateCodeChallenge } from './pkce';
import { createGetTokenBody, createGetTokenBodyFromSoMeLogin, encodeBase64 } from './utils';
import fetch from 'cross-fetch';
import { CustomStorage, LocalStorage, SessionStorage } from '~/services/storage';

/**
 * An object containing the customer's login credentials.
 * @typedef {Object} CustomerCredentials
 * @property {string} credentials.email
 * @property {string} credentials.password
 */

/**
 * Salesforce Customer object.
 * {@link https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customer}}
 * @typedef {Object} Customer
 */

const usidStorageKey = 'usid';
const encUserIdStorageKey = 'enc-user-id';
const tokenStorageKey = 'token';
const instoreTokenStorageKey = 'instore-token';
const basketIdStorageKey = 'basket-id';
const refreshTokenStorageKey = 'cc-nx';
const refreshTokenGuestStorageKey = 'cc-nx-g';
const oidStorageKey = 'oid';
const dwSessionIdKey = 'dwsid';
const REFRESH_TOKEN_COOKIE_AGE = 90; // 90 days. This value matches SLAS cartridge.

const EXPIRED_TOKEN = 'EXPIRED_TOKEN';
const INVALID_TOKEN = 'invalid refresh_token';
const CUSTOMER_INVALID = 'The customer is invalid';
const INVALID_ORG = 'invalid organizationId';

/**
 * getCallbackUrl is written to handle switching to preprod01 host as callbackuri
 * only when domain is indeed preprod, others will remain the same.
 */
const getCallbackUrl = (config: CombinedCommerceApiConfig, onClient = false) => {
    // Only modify URL if we're on preprod
    if (
        onClient &&
        typeof window !== 'undefined' &&
        window.location.hostname.includes('preprod01')
    ) {
        return `https://${window.location.hostname}${slasCallbackEndpoint}`;
    }
    // Default behavior for all other environments
    return `${config.parameters.publicUrl}${slasCallbackEndpoint}`;
};

export const errorsToKillToken = [EXPIRED_TOKEN, INVALID_TOKEN, CUSTOMER_INVALID, INVALID_ORG];

/**
 * A  class that provides auth functionality for pwa.
 */
export const slasCallbackEndpoint = '/callback';

const saveGuestCredentials = () => {
    const currentToken = localStorage.getItem('token');
    const currentBasketId = localStorage.getItem('basketId');
    if (currentToken && currentBasketId) {
        localStorage.setItem('guestBasketId', currentBasketId);
        localStorage.setItem('guestToken', currentToken);
        localStorage.setItem('mergeBasket', 'true');
    }
};

class Auth {
    private _api: CommerceAPI;
    private _config: CombinedCommerceApiConfig;
    private _onClient: boolean;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    private _pendingAuth: any | undefined;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    private _customerId: string | undefined;
    private _oid: string | null | undefined;
    _pendingLogin: any;
    private _storage: CustomStorage | Map<any, any>;
    private _sessionStorage: CustomStorage;

    constructor(api: CommerceAPI, ls?: CustomStorage) {
        this._api = api;
        this._config = api._config;
        this._onClient = typeof window !== 'undefined';
        this._pendingAuth = undefined;
        this._customerId = undefined;

        // To store tokens as cookies
        // change the next line to
        // this._storage = this._onClient ? new CookieStorage() : new Map()
        this._storage = ls ?? (this._onClient ? new LocalStorage() : new Map());
        this._sessionStorage = ls ?? (this._onClient ? new SessionStorage() : new Map());
        const configOid = api._config.parameters.organizationId;
        this._oid = this._storage.get(oidStorageKey) || configOid;

        if (this._oid !== configOid) {
            this._clearAuth();
            this._saveOid(configOid);
        }

        this.login = this.login.bind(this);
        this.logout = this.logout.bind(this);
    }

    /**
     * Returns the api client configuration
     * @returns {boolean}
     */
    get pendingLogin() {
        return this._pendingLogin;
    }

    get authToken() {
        return this._storage.get(tokenStorageKey);
    }

    get instoreAgentAccessToken(): string | undefined {
        return this._sessionStorage.get(instoreTokenStorageKey);
    }
    get basketId(): string | undefined {
        return this._sessionStorage.get(basketIdStorageKey);
    }

    get onClient() {
        return this._onClient;
    }

    get refreshToken() {
        return (
            this._storage.get(refreshTokenStorageKey) ||
            this._storage.get(refreshTokenGuestStorageKey)
        );
    }

    get usid() {
        return this._storage.get(usidStorageKey);
    }

    get encUserId() {
        return this._storage.get(encUserIdStorageKey);
    }

    get oid() {
        return this._storage.get(oidStorageKey);
    }

    /**
     * Called with the details from the redirect page that _loginWithCredentials returns
     * I think it's best we leave it to developers on how and where to call from
     * @param {{grantType, code, usid, codeVerifier, redirectUri}} requestDetails - The cutomerId of customer to get.
     */
    async getLoggedInToken(requestDetails: {
        grantType: string;
        code: string;
        usid: string;
        codeVerifier: string;
        redirectUri: string;
    }) {
        const data = new URLSearchParams();
        const { grantType, code, usid, codeVerifier, redirectUri } = requestDetails;
        data.append('code', code);
        data.append('grant_type', grantType);
        data.append('usid', usid);
        data.append('code_verifier', codeVerifier);
        data.append('channel_id', this._config.parameters.channelId);
        data.append('client_id', this._config.parameters.clientId);
        data.append('redirect_uri', redirectUri);

        const options = {
            headers: {
                'Content-Type': `application/x-www-form-urlencoded`,
            },
            body: data,
        };

        const response = await this._api.shopperLogin.getAccessToken(options);
        // Check for error response before handling the token
        if (response.status_code) {
            throw new HTTPError(response.status_code, response.message);
        }
        this._handleShopperLoginTokenResponse(response);
        return response;
    }

    /**
     * Make a post request to the OCAPI /session endpoint to bridge the session.
     *
     * The HTTP response contains a set-cookie header which sets the dwsid session cookie.
     * This cookie is used on SFRA site and it shoppers to navigate between SFRA site and
     * this PWA site seamlessly, this is often used to enable hybrid deployment.
     *
     * (Note: this method is client side only, b/c MRT doesn't support set-cookie header right now)
     *
     * @returns {Promise}
     */
    createOCAPISession() {
        return fetch(
            `${this._config.ocapiHost}/s/${this._config.parameters.siteId}/dw/shop/v21_3/sessions`,
            {
                method: 'POST',
                headers: {
                    Authorization: this.authToken,
                },
            },
        );
    }

    /**
     * Authorizes the customer as a registered or guest user.
     * @param {CustomerCredentials} [credentials]
     * @returns {Promise}
     */
    async login(credentials?: CustomerCredentials) {
        // Calling login while its already pending will return a reference
        // to the existing promise.
        if (this._pendingLogin) {
            return this._pendingLogin;
        }

        let retries = 0;
        const startLoginFlow = () => {
            let authorizationMethod = '_loginAsGuest';
            if (credentials) {
                authorizationMethod = '_loginWithCredentials';
            } else if (this.refreshToken) {
                authorizationMethod = '_refreshAccessToken';
            }
            return (
                (this as any)
                    // eslint-disable-next-line no-unexpected-multiline
                    [authorizationMethod](credentials)
                    .catch((error: Error) => {
                        if (retries === 0 && errorsToKillToken.includes(error.message)) {
                            retries = 1; // we only retry once
                            this._clearAuth();
                            return startLoginFlow();
                        }
                        throw error;
                    })
                    .then((result: any) => {
                        this._onClient && this.createOCAPISession();
                        return result;
                    })
            );
        };

        this._pendingLogin = startLoginFlow().finally(() => {
            // When the promise is resolved, we need to remove the reference so
            // that subsequent calls to `login` can proceed.
            this._pendingLogin = undefined;
        });

        return this._pendingLogin;
    }

    /**
     * Clears the stored auth token and optionally logs back in as guest.
     * @param {boolean} [shouldLoginAsGuest=true] - Indicates if we should automatically log back in as a guest
     * @returns {(Promise<Customer>|undefined)}
     */
    async logout(shouldLoginAsGuest = true) {
        const options = {
            parameters: {
                refresh_token: this.refreshToken,
                client_id: this._config.parameters.clientId,
                channel_id: this._config.parameters.siteId,
            },
        };
        await this._api.shopperLogin.logoutCustomer(options, true);
        await this._clearAuth();
        if (shouldLoginAsGuest) {
            return this.login();
        }
    }

    async requestAccountToken() {
        const url = `/api/salesforce/account/access_token`;

        const d = new URLSearchParams();
        d.append('grant_type', 'client_credentials');
        d.append(
            'scope',
            `SALESFORCE_COMMERCE_API:${this._config.parameters.organizationId.replace(
                'f_ecom_',
                '',
            )} sfcc.products.rw sfcc.orders.rw sfcc.shopper-customers.login sfcc.shopper-customers.register`,
        );

        const tokenCall = await fetch(url, {
            method: 'POST',
            body: d,
            headers: {
                'Content-Type': `application/x-www-form-urlencoded`,
            },
        });
        if (!tokenCall.ok) {
            throw new HTTPError(tokenCall.status, await tokenCall.text());
        }
        const { access_token, token_type } = await tokenCall.json();

        return { access_token, token_type };
    }

    async requestInstoreAgentToken() {
        const url = `/s/imerco/getagenttoken`;

        const d = new URLSearchParams();
        d.append('client_id', this._config.parameters.clientId);
        d.append(
            'grant_type',
            'urn:demandware:params:oauth:grant-type:client-id:dwsid:dwsecuretoken',
        );

        const tokenCall = await fetch(this._config.ocapiHost + url + '?' + d, {
            method: 'GET',
            headers: {
                'Content-Type': `application/x-www-form-urlencoded`,
                Authorization: this._api.auth.authToken,
            },
        });
        if (!tokenCall.ok) {
            throw new HTTPError(tokenCall.status, await tokenCall.text());
        }
        const { value } = await tokenCall.json();

        this._saveInstoreAgentAccessToken(value);
    }

    async requestResetPassword(email: string) {
        const { access_token, token_type } = await this.requestAccountToken();
        const body = {
            identification: email,
            type: 'login',
        };

        const resetCall = await fetch(
            `${this._config.ocapiHost}/s/${this._config.parameters.siteId}/dw/shop/v21_3/customers/password_reset`,
            {
                method: 'POST',
                headers: {
                    Authorization: token_type + ' ' + access_token,
                    'Content-Type': `application/json`,
                    'x-dw-client-id': this._config.parameters.accountClientId,
                },
                body: JSON.stringify(body),
            },
        );
        if (!resetCall.ok) {
            throw new HTTPError(resetCall.status, await resetCall.text());
        }
    }

    async setPasswordFromToken(email: string, token: string, password: string) {
        const { access_token, token_type } = await this.requestAccountToken();

        const body = {
            newPassword: password,
            resetToken: token,
            login: email,
        };

        const resetCall = await fetch(
            `${this._config.proxy}/customer/shopper-customers/v1/organizations/${this._config.parameters.organizationId}/customers/password/actions/reset?siteId=${this._config.parameters.siteId}`,
            {
                method: 'POST',
                headers: {
                    Authorization: token_type + ' ' + access_token,
                    'Content-Type': `application/json`,
                    'x-dw-client-id': this._config.parameters.accountClientId,
                },
                body: JSON.stringify(body),
            },
        );
        if (!resetCall.ok) {
            throw new HTTPError(resetCall.status, await resetCall.text());
        }
    }

    /**
     * Handles Response from ShopperLogin GetAccessToken, calls the getCustomer method and removes the PCKE code verifier from session storage
     * @private
     * @param {object} tokenResponse - access_token,id_token,refresh_token, expires_in,token_type, usid, customer_id, enc_user_id, idp_access_token
     */
    _handleShopperLoginTokenResponse(tokenResponse: any) {
        const { access_token, refresh_token, customer_id, usid, enc_user_id, id_token } =
            tokenResponse;
        this._customerId = customer_id;
        this._saveAccessToken(`Bearer ${access_token}`);
        this._saveUsid(usid);
        // we use id_token to distinguish guest and registered users
        if (id_token.length > 0) {
            this._saveEncUserId(enc_user_id);
            this._saveRefreshToken(refresh_token, 'registered');
        } else {
            this._saveRefreshToken(refresh_token, 'guest');
        }

        if (this._onClient) {
            sessionStorage.removeItem('codeVerifier');
        }
    }

    /**
     * Begins oAuth PCKE Flow
     * @param {{email, password}}} credentials - User Credentials.
     * @returns {object} - a skeleton registered customer object that can be used to retrieve a complete customer object
     */
    async _loginWithCredentials(credentials: CustomerCredentials) {
        saveGuestCredentials();
        const codeVerifier = createCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const callbackUrl = getCallbackUrl(this._config, this._onClient);

        sessionStorage.setItem('codeVerifier', codeVerifier);

        const authorization = `Basic ${encodeBase64(
            `${credentials.email}:${credentials.password}`,
        )}`;

        const data = new URLSearchParams();
        data.append('redirect_uri', callbackUrl);
        data.append('client_id', this._config.parameters.clientId);
        data.append('code_challenge', codeChallenge);
        data.append('channel_id', this._config.parameters.siteId);
        const options = {
            headers: {
                Authorization: authorization,
                'Content-Type': `application/x-www-form-urlencoded`,
            },
            body: data,
        };

        const response = await this._api.shopperLogin.authenticateCustomer(options, true);
        if (response.status >= 400) {
            const json = await response.json();
            throw new HTTPError(response.status, json.message);
        }

        const tokenBody = createGetTokenBody(
            response.url,
            callbackUrl,
            window.sessionStorage.getItem('codeVerifier')!,
        );

        const { customer_id } = await this.getLoggedInToken(tokenBody);
        const customer = {
            customerId: customer_id,
            authType: 'registered',
        };

        return customer;
    }

    async _loginWithExternal(hint: string) {
        saveGuestCredentials();
        const codeVerifier = createCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const callbackUrl = getCallbackUrl(this._config, this._onClient);

        if (this._onClient) {
            sessionStorage.setItem('codeVerifier', codeVerifier);
        }

        const options = {
            headers: {
                Authorization: this.authToken,
                'Content-Type': `application/x-www-form-urlencoded`,
            },
            parameters: {
                redirect_uri: callbackUrl,
                channel_id: this._config.parameters.siteId,
                client_id: this._config.parameters.clientId,
                response_type: 'code',
                code_challenge: codeChallenge,
                hint: hint,
            },
        };

        return (
            this._config.proxy +
            `/shopper/auth/v1/organizations/${
                this._config.parameters.organizationId
            }/oauth2/authorize?${new URLSearchParams(options.parameters)}`
        );
    }

    async loginFacebook() {
        return await this._loginWithExternal('facebook');
    }

    async loginGoogle() {
        return await this._loginWithExternal('google');
    }

    async acceptSoMeLogin(
        usid: string,
        code: string,
    ): Promise<{ customerId: string; authType: string }> {
        const callbackUrl = getCallbackUrl(this._config, this._onClient);
        const tokenBody = createGetTokenBodyFromSoMeLogin(
            usid,
            code,
            callbackUrl,
            this._onClient ? window.sessionStorage.getItem('codeVerifier')! : '',
        );

        const { customer_id } = await this.getLoggedInToken(tokenBody);

        // A guest customerId will never return a customer from the customer endpoint
        const customer = {
            authType: 'registered',
            customerId: customer_id,
        };

        return customer;
    }

    /**
     * Begins oAuth PCKE Flow for guest
     * @returns {object} - a guest customer object
     */
    async _loginAsGuest() {
        const codeVerifier = createCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        const callbackUrl = getCallbackUrl(this._config, this._onClient);

        if (this._onClient) {
            sessionStorage.setItem('codeVerifier', codeVerifier);
        }

        const options = {
            headers: {
                Authorization: '',
                'Content-Type': `application/x-www-form-urlencoded`,
            },
            parameters: {
                redirect_uri: callbackUrl,
                client_id: this._config.parameters.clientId,
                channel_id: this._config.parameters.channelId,
                code_challenge: codeChallenge,
                response_type: 'code',
                hint: 'guest',
            },
        };

        const response = await this._api.shopperLogin.authorizeCustomer(options, true);
        if (response.status >= 400) {
            const text = await response.text();
            let errorMessage = text;
            try {
                const data = JSON.parse(text);
                if (data.message) {
                    errorMessage = data.message;
                }
            } catch {} // eslint-disable-line no-empty
            throw new HTTPError(response.status, errorMessage);
        }

        const tokenBody = createGetTokenBody(
            response.url,
            callbackUrl,
            this._onClient ? window.sessionStorage.getItem('codeVerifier') : codeVerifier,
        );

        const { customer_id } = await this.getLoggedInToken(tokenBody);

        // A guest customerId will never return a customer from the customer endpoint
        const customer = {
            authType: 'guest',
            customerId: customer_id,
        };

        return customer;
    }

    /**
     * Creates a guest session
     * @private
     * @returns {*} - The response to be passed back to original caller.
     */
    async _createGuestSession() {
        const loginType = 'guest';
        const options = {
            body: {
                type: loginType,
            },
        };

        const rawResponse = await this._api.shopperCustomers.authorizeCustomer(options, true);
        return rawResponse;
    }

    /**
     * Refreshes Logged In Token
     * @private
     * @returns {<Promise>} - Handle Shopper Login Promise
     */
    async _refreshAccessToken() {
        const data = new URLSearchParams();
        data.append('grant_type', 'refresh_token');
        data.append('refresh_token', this.refreshToken);
        data.append('client_id', this._config.parameters.clientId);

        const options = {
            headers: {
                'Content-Type': `application/x-www-form-urlencoded`,
            },
            body: data,
        };
        const response = await this._api.shopperLogin.getAccessToken(options);
        // Check for error response before handling the token
        if (response.status_code) {
            throw new HTTPError(response.status_code, response.message);
        }
        this._handleShopperLoginTokenResponse(response);

        const { id_token, enc_user_id, customer_id } = response;
        const customer = {
            authType: 'guest',
            customerId: customer_id,
        };
        // Determining if registered customer or guest
        if (id_token.length > 0 && enc_user_id.length > 0) {
            customer.authType = 'registered';
        }
        return customer;
    }
    /**
     * Stores the given auth token.
     * @private
     * @param {string} token - A JWT auth token.
     */
    _saveAccessToken(token: string) {
        this._storage.set(tokenStorageKey, token);
    }

    _saveInstoreAgentAccessToken(token: string) {
        this._sessionStorage.set(instoreTokenStorageKey, token);
    }

    saveBasketId(id?: string) {
        if (!id) {
            this._sessionStorage.delete(basketIdStorageKey);
        } else {
            this._sessionStorage.set(basketIdStorageKey, id);
        }
    }

    /**
     * Stores the given usid token.
     * @private
     * @param {string} usid - Unique shopper Id.
     */
    _saveUsid(usid: string) {
        this._storage.set(usidStorageKey, usid);
    }

    /**
     * Stores the given enc_user_id token. enc = encoded
     * @private
     * @param {string} encUserId - Logged in Shopper reference for Einstein API.
     */
    _saveEncUserId(encUserId: string) {
        this._storage.set(encUserIdStorageKey, encUserId);
    }

    /**
     * Stores the given oid token.
     * @private
     * @param {string} oid - Unique organization Id.
     */
    _saveOid(oid: string) {
        this._oid = oid;
        this._storage.set(oidStorageKey, oid);
    }

    /**
     * Removes the stored auth token.
     * @private
     */
    _clearAuth() {
        this._customerId = undefined;
        this._storage.delete(tokenStorageKey);
        this._storage.delete(refreshTokenStorageKey);
        this._storage.delete(refreshTokenGuestStorageKey);
        this._storage.delete(usidStorageKey);
        this._storage.delete(encUserIdStorageKey);
        this._storage.delete(dwSessionIdKey);
        this._sessionStorage.delete(basketIdStorageKey);
        this._sessionStorage.delete(instoreTokenStorageKey);
    }

    /**
     * Stores the given refresh token.
     * @private
     * @param {string} refreshToken - A JWT refresh token.
     */
    _saveRefreshToken(refreshToken: string, type: string) {
        if (type === 'registered') {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            this._storage.set(refreshTokenStorageKey, refreshToken, {
                expires: REFRESH_TOKEN_COOKIE_AGE,
            });
            this._storage.delete(refreshTokenGuestStorageKey);
            return;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        this._storage.set(refreshTokenGuestStorageKey, refreshToken, {
            expires: REFRESH_TOKEN_COOKIE_AGE,
        });
        this._storage.delete(refreshTokenStorageKey);
    }
}

export default Auth;
